import { extendTailwindMerge } from 'tailwind-merge'
import { clsx, type ClassValue } from 'clsx'

const twMerge = extendTailwindMerge({
  extend: {
    theme: {
      text: ['display-xs', 'display-sm', 'display-md', 'display-lg', 'display-xl', 'display-2xl'],
    },
  },
})

/**
 * Combines clsx and tailwind-merge for conditional class merging.
 * Use this for combining Tailwind classes where conflicts should be resolved.
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Alias for cn - wrapper around twMerge for class merging.
 * Used to merge classes inside style objects.
 */
export const cx = twMerge

/**
 * Helper function for sorting classes in style objects.
 * This helps with Tailwind IntelliSense sorting which isn't supported by default.
 */
export function sortCx<
  T extends Record<string, string | number | Record<string, string | number | Record<string, string | number>>>
>(classes: T): T {
  return classes
}

/**
 * Check if a value is a React component (function or class component).
 */
export function isReactComponent(
  value: unknown
): value is React.ComponentType<unknown> {
  if (typeof value === 'function') {
    // Check for class components
    if (value.prototype && value.prototype.isReactComponent) {
      return true
    }
    // Function components
    return true
  }
  return false
}

/**
 * Format a duration in seconds to a human-readable string.
 */
export function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = Math.floor(seconds % 60)

  if (hours > 0) {
    return `${hours}h ${minutes}m ${secs}s`
  }
  if (minutes > 0) {
    return `${minutes}m ${secs}s`
  }
  return `${secs}s`
}

/**
 * Sleep for a given number of milliseconds.
 */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Truncate a string to a given length with ellipsis.
 */
export function truncate(str: string, length: number): string {
  if (str.length <= length) return str
  return str.slice(0, length) + '...'
}

/** True when running in Safari (desktop or iOS) but NOT Chrome/Edge/Firefox. */
export function isSafari(): boolean {
  const ua = navigator.userAgent
  return /Safari/.test(ua) && !/Chrome|CriOS|FxiOS|Edg/.test(ua)
}
